import { useCallback, useState } from "react";
import { useNavigate, useOutletContext } from "react-router-dom";
import { Copy, Trash2 } from "lucide-react";
import { mutate } from "swr";
import { Button } from "~/components/ui/button";
import useToast from "~/components/ui/toast";
import { SettingsRow } from "~/pages/manifold-nextgen/components/settings/SettingsRow";
import { SettingsSection } from "~/pages/manifold-nextgen/components/settings/SettingsSection";
import { useCanDeleteAgent } from "~/pages/manifold-nextgen/hooks/useCanDeleteAgent";
import { useCanDuplicateAgent } from "~/pages/manifold-nextgen/hooks/useCanDuplicateAgent";
import { useDuplicateAgent } from "~/pages/manifold-nextgen/hooks/useDuplicateAgent";
import { ROUTE_NEXTGEN_VIEW } from "~/router/constants";
import { Agent, agentListPath, deleteAgent } from "~/services/agents";
import { DeleteAgentDialog } from "./DeleteAgentDialog";

export const AgentSettings = () => {
  const agent = useOutletContext<Agent>();
  const navigate = useNavigate();
  const { showSuccess, showError } = useToast();
  const canDuplicate = useCanDuplicateAgent(agent);
  const canDelete = useCanDeleteAgent(agent);
  const duplicateAgentAction = useDuplicateAgent();

  const [deleteOpen, setDeleteOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleDuplicate = useCallback(
    () => duplicateAgentAction(agent.id, agent.display_name),
    [duplicateAgentAction, agent.id, agent.display_name]
  );

  const handleDelete = async () => {
    setIsDeleting(true);
    try {
      await deleteAgent(agent.id);
      await mutate(agentListPath());
      showSuccess(`"${agent.display_name}" deleted`);
      navigate(`${ROUTE_NEXTGEN_VIEW}/agents`);
    } catch {
      showError("Failed to delete agent");
    } finally {
      setIsDeleting(false);
      setDeleteOpen(false);
    }
  };

  return (
    <div className="mx-auto flex max-w-3xl flex-col gap-6">
      {canDuplicate && (
        <SettingsSection title="General">
          <SettingsRow title="Duplicate agent" description="Create an editable copy of this agent.">
            <Button variant="outline" onClick={handleDuplicate}>
              <Copy className="size-4" />
              Duplicate
            </Button>
          </SettingsRow>
        </SettingsSection>
      )}

      {canDelete && (
        <SettingsSection title="Danger Zone" danger>
          <SettingsRow
            title="Delete agent"
            description="Permanently delete this agent. This action cannot be undone."
          >
            <Button variant="destructive" onClick={() => setDeleteOpen(true)}>
              <Trash2 className="size-4" />
              Delete
            </Button>
          </SettingsRow>
        </SettingsSection>
      )}

      <DeleteAgentDialog
        agent={agent}
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        onConfirm={handleDelete}
        isDeleting={isDeleting}
      />
    </div>
  );
};
